import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ManageTasksChatPage } from 'src/pages/ManageTasksChatPage';
import { SchedulerAccessModal } from 'src/pages/AuthPages/components/SchedulerAccessModal';
import {
  useChatHistory,
  useGoogleResourcesAccess,
  useRightSidePanelActions,
  useSession,
  useTeamInfo,
} from 'src/hooks';
import { UserStatus } from 'src/types/models/UserStatus';
import { PAYPAL_PAYMENT_IN_PROGRESS, VERSION_NUMBER } from 'src/constants';
import { MaintenanceScreen } from 'src/pages/AuthPages/components/MaintenanceScreen';
import { QueueScreen } from 'src/pages/AuthPages/components/QueueScreen';
import { UpdatesModal } from 'src/pages/AuthPages/components/UpdatesModal';
import {
  AppRoutes,
  UserSettingsNestedRoutes,
  RouteItem,
  APINestedRoutes,
} from 'src/types';
import { ManageAccountPage } from 'src/pages/ManageAccountPage';
import { SubscriptionPlans } from 'src/pages/ManageAccountPage/components/SubscriptionPlans';
import { UpdatePlanModal } from 'src/components/UpdatePlanModal';
import { useCanConnectToGoogle } from 'src/hooks/useCanConnectToGoogle';
import { env } from 'src/env';
import { Profile } from 'src/pages/ManageAccountPage/components/Profile';
import { Integrations } from 'src/pages/ManageAccountPage/components/Integrations';
import { General } from 'src/pages/ManageAccountPage/components/General';
import { ContactNinjaModal } from 'src/components/ContactNinjaModal';
import { getWithExpiry } from 'src/utils/localStorage';
import { CancelPaypalSubscriptionModal } from 'src/components/CancelPaypalSubscriptionModal';
import { ManageUsers } from 'src/v2/pages/manageUsersInWorkspace';
import { WelcomeTeamOwnerScreen } from 'src/v2/components/WelcomeTeamOwnerScreen';
import { InviteUsersModal } from 'src/v2/pages/manageUsersInWorkspace/components/InviteUsersModal';
import { InviteToTeamPage } from 'src/v2/pages/invitation';
import { JoinWorkspaceModal } from 'src/v2/components/modals/JoinWorkspaceModal';
import { useCheckIfUserInvitedToTeam } from 'src/hooks/teamsHooks/useCheckIfUserInvitedToTeam';
import { AmpOnboardingPage } from 'src/v2/pages/auth/AmpOnboardingPage';
import { InvitationAccessDeniedModal } from 'src/v2/components/modals/InvitationAccessDeniedModal';
import { RequestUpgradeModal } from 'src/v2/components/modals/RequestUpgradeModal';
import { PurchaseSeatsModal } from 'src/v2/components/modals/PurchaseSeatsModal';
import { GetEnterpriseModal } from 'src/v2/components/modals/GetEnterpriseModal';
import { Loading } from 'src/components/Loading';
import { UnableJoinTeamModal } from 'src/v2/components/modals/UnableJoinTeamModal';
import { NotFound } from 'src/pages/NotFound';
import { Logout } from 'src/pages/Logout';
import { APIKeys } from 'src/pages/ManageAccountPage/components/APIKeys';
import { APIUsage } from 'src/pages/ManageAccountPage/components/APIUsage';
import { PageLayoutWithNavMenu } from 'src/components/PageLayoutWithNavMenu';
import { CancelSubscription } from 'src/v2/pages/subscription/components/CancelSubscription';
import { TeamStatus } from 'src/types/models/TeamStatus';
import { BlockedTeamMemberScreen } from 'src/v2/components/BlockedTeamMemberScreen';
import { UpdatePaymentPage } from 'src/pages/UpdatePaymentPage';
import { ReferralPage } from 'src/v2/pages/referral';
import { PaymentPageWrapper } from 'src/pages/PaymentBetaPage';
import { AddOnPage } from 'src/pages/AddOnPage';
import { AddOnCredits } from 'src/pages/AddOnPage/components/AddOnCredits';
import { SupportModal } from 'src/components/SupportModal';
import { useFeatureFlagEnabled } from 'posthog-js/react';

const ENTERPRISE_PRIVATE_ROUTES = [
  AppRoutes.HOME,
  AppRoutes.MANAGE_ACCOUNT,
  AppRoutes.NOT_FOUND,
];

const ENTERPRISE_MANAGE_ACCOUNT_ROUTES: string[] = [
  UserSettingsNestedRoutes.PROFILE,
  UserSettingsNestedRoutes.INTEGRATIONS,
  UserSettingsNestedRoutes.GENERAL,
  UserSettingsNestedRoutes.SUBSCRIPTION,
  APINestedRoutes.API_KEYS,
  APINestedRoutes.API_USAGE,
];

const defaultRoutes: RouteItem[] = [
  {
    element: <PageLayoutWithNavMenu />,
    children: [
      {
        path: AppRoutes.HOME,
        element: <ManageTasksChatPage />,
      },
      {
        path: AppRoutes.NOT_FOUND,
        element: <NotFound />,
      },
      {
        path: AppRoutes.LOGOUT,
        element: <Logout />,
      },
      {
        path: AppRoutes.MANAGE_ACCOUNT,
        element: <ManageAccountPage />,
        children: [
          {
            index: true,
            element: <SubscriptionPlans />,
          },
          {
            path: UserSettingsNestedRoutes.PROFILE,
            element: <Profile />,
          },
          {
            path: UserSettingsNestedRoutes.INTEGRATIONS,
            element: <Integrations />,
          },
          {
            path: UserSettingsNestedRoutes.GENERAL,
            element: <General />,
          },
          {
            path: UserSettingsNestedRoutes.SUBSCRIPTION,
            element: <SubscriptionPlans />,
          },
          {
            path: UserSettingsNestedRoutes.MANAGE_USERS,
            element: <ManageUsers />,
          },
        ],
      },
      // {
      //   path: AppRoutes.VIDEO_TUTORIALS,
      //   element: <VideoTutorialsPage />,
      // },
    ],
  },
  {
    path: AppRoutes.PAYMENT,
    element: <PaymentPageWrapper />,
  },
  {
    path: AppRoutes.INVITE,
    element: <InviteToTeamPage />,
  },
  {
    path: AppRoutes.AMP_ONBOARD,
    element: <AmpOnboardingPage />,
  },
  {
    path: AppRoutes.CANCEL_SUBSCRIPTION,
    element: <CancelSubscription />,
  },
  {
    path: AppRoutes.REFERRAL,
    element: <ReferralPage />,
  },
  {
    element: <AddOnPage />,
    children: [
      {
        path: AppRoutes.ADD_ON,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <AddOnCredits />,
          },
          {
            path: APINestedRoutes.CREDITS,
            element: <AddOnCredits />,
          },
          {
            path: APINestedRoutes.API_KEYS,
            element: <APIKeys />,
          },
          {
            path: APINestedRoutes.API_USAGE,
            element: <APIUsage />,
          },
        ],
      },
    ],
  },
];

const defaultRoutesV2: RouteItem[] = [
  {
    element: <PageLayoutWithNavMenu />,
    children: [
      {
        path: AppRoutes.HOME,
        element: <ManageTasksChatPage />,
      },
      {
        path: AppRoutes.NOT_FOUND,
        element: <NotFound />,
      },
      {
        path: AppRoutes.LOGOUT,
        element: <Logout />,
      },
      // {
      //   path: AppRoutes.VIDEO_TUTORIALS,
      //   element: <VideoTutorialsPage />,
      // },
    ],
  },
  {
    element: <ManageAccountPage />,
    children: [
      {
        index: true,
        element: <SubscriptionPlans />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.PROFILE}`,
        element: <Profile />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.MANAGE_USERS}`,
        element: <ManageUsers />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.SUBSCRIPTION}`,
        element: <SubscriptionPlans />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.INTEGRATIONS}`,
        element: <Integrations />,
      },
    ],
  },
  {
    path: AppRoutes.PAYMENT,
    element: <PaymentPageWrapper />,
  },
  {
    path: AppRoutes.INVITE,
    element: <InviteToTeamPage />,
  },
  {
    path: AppRoutes.AMP_ONBOARD,
    element: <AmpOnboardingPage />,
  },
  {
    path: AppRoutes.CANCEL_SUBSCRIPTION,
    element: <CancelSubscription />,
  },
  {
    path: AppRoutes.REFERRAL,
    element: <ReferralPage />,
  },
  {
    element: <AddOnPage />,
    children: [
      {
        path: AppRoutes.ADD_ON,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <AddOnCredits />,
          },
          {
            path: APINestedRoutes.CREDITS,
            element: <AddOnCredits />,
          },
          {
            path: APINestedRoutes.API_KEYS,
            element: <APIKeys />,
          },
          {
            path: APINestedRoutes.API_USAGE,
            element: <APIUsage />,
          },
        ],
      },
    ],
  },
];

const defaultRoutesV3: RouteItem[] = [
  {
    element: <PageLayoutWithNavMenu />,
    children: [
      {
        path: AppRoutes.HOME,
        element: <ManageTasksChatPage />,
      },
      {
        path: AppRoutes.CHAT_HISTORY,
        element: <ManageTasksChatPage />,
      },
      {
        path: AppRoutes.NOT_FOUND,
        element: <NotFound />,
      },
      {
        path: AppRoutes.LOGOUT,
        element: <Logout />,
      },
      // {
      //   path: AppRoutes.VIDEO_TUTORIALS,
      //   element: <VideoTutorialsPage />,
      // },
    ],
  },
  {
    element: <ManageAccountPage />,
    children: [
      {
        index: true,
        element: <SubscriptionPlans />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.PROFILE}`,
        element: <Profile />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.MANAGE_USERS}`,
        element: <ManageUsers />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.SUBSCRIPTION}`,
        element: <SubscriptionPlans />,
      },
      {
        path: `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.INTEGRATIONS}`,
        element: <Integrations />,
      },
    ],
  },
  {
    path: AppRoutes.PAYMENT,
    element: <PaymentPageWrapper />,
  },
  {
    path: AppRoutes.INVITE,
    element: <InviteToTeamPage />,
  },
  {
    path: AppRoutes.AMP_ONBOARD,
    element: <AmpOnboardingPage />,
  },
  {
    path: AppRoutes.CANCEL_SUBSCRIPTION,
    element: <CancelSubscription />,
  },
  {
    path: AppRoutes.REFERRAL,
    element: <ReferralPage />,
  },
  {
    element: <AddOnPage />,
    children: [
      {
        path: AppRoutes.ADD_ON,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <AddOnCredits />,
          },
          {
            path: APINestedRoutes.CREDITS,
            element: <AddOnCredits />,
          },
          {
            path: APINestedRoutes.API_KEYS,
            element: <APIKeys />,
          },
          {
            path: APINestedRoutes.API_USAGE,
            element: <APIUsage />,
          },
        ],
      },
    ],
  },
];

export const PrivateContent = () => {
  const [isShowUpdatesModal, setIsShowUpdatesModal] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const location = useLocation();
  const { isNinjaRetired } = useChatHistory();
  const isNewRoutesStructure = useFeatureFlagEnabled('hideLeftPanelInMN');

  // checking if user was invited to team (if yes, show accept/decline modal)
  const { isInviteLoading } = useCheckIfUserInvitedToTeam();

  const {
    appUser,
    isFirstSchedulerTask,
    isAccessModalShown,
    updateIsAccessModalShown,
    isEnterpriseTier,
    isTeamsTier,
  } = useSession();

  const { isOwner, teamInfo } = useTeamInfo();

  const canConnectToGoogle = useCanConnectToGoogle();

  const {
    canAccessDirectory,
    canAccessContacts,
    canAccessCalendar,
    isLoadingConnectionCheck,
    isFetchingConnectionCheck,
  } = useGoogleResourcesAccess();

  const isShowAccess =
    canConnectToGoogle &&
    isFirstSchedulerTask &&
    !isAccessModalShown &&
    !isLoadingConnectionCheck &&
    !isFetchingConnectionCheck &&
    !!appUser?.user_id &&
    (!canAccessDirectory || !canAccessContacts || !canAccessCalendar);

  const isBlockedOwner = isOwner && teamInfo?.status === TeamStatus.SUSPENDED;
  const isBlockedMember =
    isTeamsTier && !isOwner && teamInfo?.status === TeamStatus.SUSPENDED;

  const { resetAndCloseRightSidePanelData } = useRightSidePanelActions();

  useEffect(() => {
    if (location.pathname !== AppRoutes.HOME) {
      resetAndCloseRightSidePanelData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    // only show the updates modal in beta and production
    if (
      env.REACT_APP_ENVIRONMENT === 'beta' ||
      env.REACT_APP_ENVIRONMENT === 'gamma' ||
      env.REACT_APP_ENVIRONMENT === 'production'
    ) {
      if (
        !!appUser?.metadata?.app_version &&
        appUser?.metadata?.app_version.trim() !== VERSION_NUMBER
      ) {
        setIsShowUpdatesModal(true);
      }
    }
  }, [appUser?.metadata?.app_version, appUser?.user_id]);

  // solely to remove this key if it's expired but haven't removed by the app explicitly
  useEffect(() => {
    getWithExpiry(PAYPAL_PAYMENT_IN_PROGRESS);
  }, [appUser?.user_id]);

  useEffect(() => {
    if (
      appUser?.status !== UserStatus.MAINTENANCE &&
      appUser?.metadata?.user_master_status !== UserStatus.MAINTENANCE
    ) {
      setMaintenanceMode(false);
    } else {
      setMaintenanceMode(true);
    }
  }, [appUser?.metadata?.user_master_status, appUser?.status]);

  const routes = useMemo(() => {
    if (!isEnterpriseTier) {
      return isNinjaRetired
        ? defaultRoutesV3
        : isNewRoutesStructure
          ? defaultRoutesV2
          : defaultRoutes;
    }

    return (
      isNinjaRetired
        ? defaultRoutesV3
        : isNewRoutesStructure
          ? defaultRoutesV2
          : defaultRoutes
    ).filter((route) => {
      if (!!route.path) {
        return ENTERPRISE_PRIVATE_ROUTES.includes(route.path as AppRoutes);
      }

      const nestedRoutes = route.children?.filter((nestedRoute) => {
        if (nestedRoute.path === AppRoutes.MANAGE_ACCOUNT) {
          const filteredChildren = nestedRoute.children?.filter((nestedChild) =>
            ENTERPRISE_MANAGE_ACCOUNT_ROUTES.includes(
              nestedChild.path as AppRoutes,
            ),
          );

          return { ...nestedRoute, children: filteredChildren };
        }

        return ENTERPRISE_PRIVATE_ROUTES.includes(
          nestedRoute.path as AppRoutes,
        );
      });

      return { ...route, children: nestedRoutes };
    });
  }, [isEnterpriseTier, isNewRoutesStructure, isNinjaRetired]);

  const handleCloseAccessModal = () => {
    updateIsAccessModalShown(true);
  };

  if (isInviteLoading) {
    return <Loading />;
  }

  if (maintenanceMode) {
    return <MaintenanceScreen />;
  }

  if (appUser?.status === UserStatus.QUEUE) {
    return <QueueScreen />;
  }

  if (isTeamsTier && isOwner && !!teamInfo && !teamInfo?.team_name) {
    return <WelcomeTeamOwnerScreen />;
  }

  if (isTeamsTier && isBlockedMember) {
    return <BlockedTeamMemberScreen />;
  }

  if (isTeamsTier && isBlockedOwner) {
    return <UpdatePaymentPage />;
  }

  return (
    <>
      <Routes>
        {routes.map((route, index) => (
          <Route key={index} element={route.element}>
            {route.children ? (
              route.children.map((childRoute, childIndex) => (
                <Route
                  key={childIndex}
                  path={childRoute.path}
                  element={childRoute.element}
                >
                  {childRoute?.children?.map((grandChild, grandChildIndex) => (
                    <Route
                      key={grandChildIndex}
                      index={grandChild.index}
                      path={grandChild.path}
                      element={grandChild.element}
                    />
                  ))}
                </Route>
              ))
            ) : (
              <Route path={route.path} element={route.element} />
            )}
          </Route>
        ))}
        <Route
          path={AppRoutes.WILDCARD}
          element={<Navigate to={AppRoutes.HOME} replace />}
        />
      </Routes>

      {!isEnterpriseTier && (
        <SchedulerAccessModal
          isOpen={isShowAccess}
          onClose={handleCloseAccessModal}
        />
      )}

      <UpdatesModal isOpen={isShowUpdatesModal} />
      <UpdatePlanModal />
      <ContactNinjaModal />
      <CancelPaypalSubscriptionModal />
      <InviteUsersModal />
      <JoinWorkspaceModal />
      <InvitationAccessDeniedModal />
      <RequestUpgradeModal />
      <PurchaseSeatsModal />
      <GetEnterpriseModal />
      <UnableJoinTeamModal />
      <SupportModal />
    </>
  );
};
